import { FC, useState } from 'react';

import CtaButton from '@components/quiz/shared/CtaButton';
import StickyCtaBar from '@components/quiz/shared/StickyCtaBar';
import { useReadiness } from '@components/quiz/ReadinessProvider';
import type { SanityReadinessLeagueNameBlock } from '@lib/sanity/types';

type LeagueNameBlockProps = SanityReadinessLeagueNameBlock & {
  onNext: () => void;
};

const DEFAULT_MAX_LENGTH = 40;

/**
 * Step 3: optional mini-league name. The name powers verdict copy and
 * the adaptive CTA; skipping is an equal-weight path.
 */
const LeagueNameBlock: FC<LeagueNameBlockProps> = ({
  inputLabel,
  inputPlaceholder,
  maxLength,
  skipTitle,
  skipSubtext,
  ctaText,
  onNext
}) => {
  const { actions } = useReadiness();
  const [name, setName] = useState('');

  const trimmedName = name.trim();

  const handleSubmit = () => {
    if (!trimmedName) return;
    actions.setLeagueName(trimmedName);
    onNext();
  };

  const handleSkip = () => {
    actions.skipLeagueName();
    onNext();
  };

  return (
    <section className="flex flex-col items-center px-6 pb-28 pt-8">
      <div className="w-full max-w-sm">
        {inputLabel && (
          <p className="mb-3 font-body text-xs font-bold uppercase tracking-widest text-neutral-500">
            {inputLabel}
          </p>
        )}
        <input
          type="text"
          value={name}
          maxLength={maxLength ?? DEFAULT_MAX_LENGTH}
          onChange={(event) => setName(event.target.value)}
          placeholder={inputPlaceholder ?? 'e.g. The Small Council'}
          data-cy="readiness-league-name-input"
          className="w-full rounded-xl border border-neutral-300 bg-white px-[18px] py-3.5 font-body text-sm text-neutral-800 outline-none transition-all focus:border-primary-600"
        />
      </div>

      <StickyCtaBar>
        <CtaButton
          onClick={handleSubmit}
          disabled={!trimmedName}
          dataCy="readiness-league-name-cta"
        >
          {ctaText ?? 'Make my verdict about this league'}
        </CtaButton>
        <button
          type="button"
          onClick={handleSkip}
          data-cy="readiness-league-name-skip"
          className="mt-3 w-full rounded-xl border-2 border-dashed border-primary-300 bg-primary-50 px-5 py-3 text-center transition-colors hover:border-primary-400"
        >
          <span className="block font-heading text-sm font-bold text-neutral-800">
            {skipTitle ?? "I'll skip for now"}
          </span>
          {skipSubtext && (
            <span className="block font-body text-xs text-neutral-500">
              {skipSubtext}
            </span>
          )}
        </button>
      </StickyCtaBar>
    </section>
  );
};

export default LeagueNameBlock;
